import { startAppAction } from '@pressreader/appcore';
import { createActionHandler, createReducer } from '@pressreader/appstore';
import { NavConfig, NavConfigRefocusConfigAny, NavConfigRefocusTargetAny, ViewName } from '@pressreader/navigation-types';
import { WithHash } from '@pressreader/types';

import { getLocation } from '../api';
import { Action, Location } from '../types';
import { areRefocusConfigsEqual, areRefocusTargetsEqual, extendRefocusTargetWithHash, isStaleNavigation } from '../utils';

import {
    navConfigUpdated,
    navigateBegan,
    navigateDone,
    navigateFailed,
    navigateInterrupted,
    refocusConfigsAddAction,
    refocusConfigsRemoveAction,
    refocusIsPerforming,
    refocusPerformed,
    refocusTargetsAddAction,
    refocusTargetsRemoveAction,
    updateLocationAction,
} from './actions';
import {
    chooseActiveRefocusTargetAndConfiguration,
    updateRefocusConfigurationsAndChooseActiveTarget,
    updateRefocusTargetsAndGetActiveTarget,
} from './reducer.refocus.utils';

interface IState {
    handleRouting: boolean;

    /**
     * Whether a navigation to a view is currently in progress.
     */
    isNavigationInProgress: boolean;

    /**
     * `NavConfig.id` of the latest navigation-relevant config (last `navigateBegan` or
     * `navConfigUpdated`). Terminal actions with a different id are stale — see `isStaleNavigation`.
     */
    currentNavId: string | null;

    viewName: ViewName | null;
    subviewName?: string;

    prevNavConfig: NavConfig | null;
    navConfig: NavConfig | null;
    navConfigHistory: NavConfig[];
    location: Location;

    /**
     * How the current navigation entered the history: 'POP' for back/forward, 'PUSH' for a new
     * entry, 'REPLACE_RELOAD' for an in-place redirect. Plain 'REPLACE' never starts a navigation
     * (it only rewrites the current entry's state mid-flight), so it never lands here.
     */
    navigationOrigin: Exclude<Action, 'REPLACE'> | null;

    refocusTargetActive: NavConfigRefocusTargetAny | null;
    refocusConfigActive: WithHash<NavConfigRefocusConfigAny> | null;
    refocusTargets: WithHash<NavConfigRefocusTargetAny>[];
    refocusConfigs: WithHash<NavConfigRefocusConfigAny>[];
    refocusingTo: NavConfigRefocusTargetAny | null;
}

const initialStateRefocus = Object.freeze({
    /**
     * Refocus target that is currently being focused.
     */
    refocusingTo: null,
    /**
     * Refocus target that should be focused.
     */
    refocusTargetActive: null,
    /**
     * Refocus configuration that is currently active.
     */
    refocusConfigActive: null,
    /**
     * Refocus targets to be used after navigation.
     */
    refocusTargets: [],
    /**
     * Refocus configurations to be used after navigation.
     */
    refocusConfigs: [],
});

const initialState: IState = {
    handleRouting: false,
    isNavigationInProgress: false,
    currentNavId: null,

    viewName: null,
    subviewName: undefined,

    prevNavConfig: null,
    navConfig: null,
    location: getLocation(),
    navigationOrigin: null,

    /**
     * History of navigation configurations. The most recent configuration is at the end of the array.
     */
    navConfigHistory: [],
    ...initialStateRefocus,
};

const NAV_HISTORY_MAX_ENTRIES = 50;
function navConfigHistoryUpdateWithNavConfig(navConfigHistory: NavConfig[], newNavConfig: NavConfig): NavConfig[] {
    const newHistory = [...navConfigHistory, newNavConfig];
    if (newHistory.length <= NAV_HISTORY_MAX_ENTRIES) {
        return newHistory;
    }
    return newHistory.slice(-NAV_HISTORY_MAX_ENTRIES);
}

// NavConfig subtypes declare their own `subview` (a per-view string enum/union); the base NavConfig
// intentionally doesn't, to avoid widening it on every config. Read it structurally for the generic
// `subviewName` used by root-level styling.
function getSubview(config: NavConfig): string | undefined {
    return (config as { subview?: string }).subview;
}

const reducer = createReducer(
    initialState,
    createActionHandler(startAppAction, (state, action) => ({
        ...state,
        handleRouting: action.payload.handleRouting ?? true,
    })),
    createActionHandler(navigateBegan, (state, action) => ({
        ...state,
        ...initialStateRefocus,
        isNavigationInProgress: true,
        currentNavId: action.payload.config.id || state.currentNavId,
        refocusTargets: state.refocusTargets,
        viewName: action.payload.config.viewName,
        subviewName: getSubview(action.payload.config),
    })),
    createActionHandler(navigateDone, (state, { payload }) => {
        if (isStaleNavigation(payload.config, state.currentNavId)) {
            return state;
        }

        const prevConfig = payload.prevConfig;
        const prevNavConfigToStore = !prevConfig || payload.config.replaced ? prevConfig : { ...prevConfig };
        const navConfigHistory = navConfigHistoryUpdateWithNavConfig(state.navConfigHistory, payload.config);

        return {
            ...state,
            isNavigationInProgress: false,
            viewName: payload.config.viewName,
            subviewName: getSubview(payload.config),
            prevNavConfig: prevNavConfigToStore,
            navConfigHistory,
            navConfig: { ...payload.config },
        };
    }),
    createActionHandler(navigateFailed, (state, { payload }) => {
        if (isStaleNavigation(payload.config, state.currentNavId)) {
            return state;
        }

        return {
            ...state,
            isNavigationInProgress: false,
            viewName: payload.config.viewName,
            subviewName: getSubview(payload.config),
        };
    }),
    createActionHandler(navigateInterrupted, (state, action) => {
        if (isStaleNavigation(action.payload.config, state.currentNavId)) {
            return state;
        }

        return {
            ...state,
            isNavigationInProgress: false,
            viewName: action.payload.config.viewName,
            subviewName: getSubview(action.payload.config),
        };
    }),
    createActionHandler(navConfigUpdated, (state, action) => {
        const newNavConfig = action.payload.newNavConfig;
        // `isStaleNavigation` is a symmetric id-mismatch check: from this side the incoming
        // config SUPERSEDES the tracked navigation rather than trailing it.
        const supersedesInFlightNavigation = state.isNavigationInProgress && isStaleNavigation(newNavConfig, state.currentNavId);

        return {
            ...state,
            // The `shouldSkipNavigation` route path mints a fresh config id without a navigateBegan
            // (popup open/close, history-back to a cached page) — re-key so in-flight terminal
            // actions of the superseded navigation read as stale.
            currentNavId: newNavConfig.id || state.currentNavId,
            // Re-keying orphans the superseded navigation's own navigateDone (the guard drops it),
            // so nothing else can ever clear the in-progress flag — close it here. Same-id mid-load
            // config rewrites (e.g. PageView page number) keep the flag untouched.
            isNavigationInProgress: supersedesInFlightNavigation ? false : state.isNavigationInProgress,
            // Required when `shouldSkipNavigation` bypasses navigateBegan/navigateDone — those normally sync viewName.
            viewName: newNavConfig.viewName,
            subviewName: getSubview(newNavConfig),
            navConfig: { ...newNavConfig },
            navConfigHistory: navConfigHistoryUpdateWithNavConfig(state.navConfigHistory, newNavConfig),
        };
    }),
    createActionHandler(updateLocationAction, (state, action) => ({
        ...state,
        location: { ...action.payload.location },
        navigationOrigin: action.payload.action === 'REPLACE' ? state.navigationOrigin : action.payload.action,
    })),
    createActionHandler(refocusConfigsAddAction, (state, action) => {
        const { refocusConfigActive, refocusConfigs, refocusTargetActive } = updateRefocusConfigurationsAndChooseActiveTarget({
            currentRefocusConfigs: state.refocusConfigs,
            newRefocusConfigurations: [action.payload],
            currentRefocusTargets: state.refocusTargets,
        });

        return {
            ...state,
            refocusConfigActive,
            refocusConfigs,
            refocusTargetActive,
        };
    }),
    createActionHandler(refocusTargetsAddAction, (state, action) => {
        const { refocusTargetActive, refocusConfigActive, refocusConfigs, refocusTargets } = updateRefocusTargetsAndGetActiveTarget({
            currentRefocusConfigs: state.refocusConfigs,
            newRefocusTargets: [action.payload],
            currentRefocusTargets: state.refocusTargets,
        });

        return {
            ...state,
            refocusTargetActive,
            refocusConfigActive,
            refocusConfigs,
            refocusTargets,
        };
    }),
    createActionHandler(refocusConfigsRemoveAction, (state, action) => {
        const refocusConfigToRemove = action.payload.config;
        const updatedRefocusConfigs = state.refocusConfigs.filter(refocusConfig => {
            return !areRefocusConfigsEqual(refocusConfig, refocusConfigToRemove);
        });
        const { refocusConfigActive, refocusTargetActive } = chooseActiveRefocusTargetAndConfiguration(state.refocusTargets, updatedRefocusConfigs);

        return {
            ...state,
            refocusTargetActive,
            refocusConfigActive,
            refocusConfigs: updatedRefocusConfigs,
        };
    }),
    createActionHandler(refocusTargetsRemoveAction, (state, action) => {
        const refocusTargetToRemoveWithHash = extendRefocusTargetWithHash(action.payload.target);
        const updatedRefocusTargets = state.refocusTargets.filter(refocusTarget => {
            return !areRefocusTargetsEqual(refocusTarget, refocusTargetToRemoveWithHash);
        });
        const { refocusConfigActive, refocusTargetActive } = chooseActiveRefocusTargetAndConfiguration(updatedRefocusTargets, state.refocusConfigs);

        return {
            ...state,
            refocusTargetActive,
            refocusConfigActive,
            refocusTargets: updatedRefocusTargets,
        };
    }),
    createActionHandler(refocusIsPerforming, (state, action) => {
        return {
            ...state,
            refocusingTo: action.payload,
        };
    }),
    createActionHandler(refocusPerformed, (state, _action) => {
        return {
            ...state,
            ...initialStateRefocus,
            refocusTargets: state.refocusTargets,
        };
    }),
);

export { initialState, IState, reducer };
